import React, { useMemo } from 'react';

import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';

import { LbMenu } from '@infoods/lifebase-ui';
import { MenuOptions } from '../../../../../../general-types';
import { Flags } from '../../../../../../utils-types';
import {
  INBODY_TYPE_WITH_DISABLED_SCENARIO,
  InbodyType,
} from '../../../../../../shared/constants/inbody-info/InbodyType';
import { ScanSummaryModal } from '../../../../../containers/NgzScanSummaryModal/NgzScanSummaryModal';
import { ScanMetadata } from '../../../../../interfaces/scanMetadata';
import { openModal } from '../../../../../../shared/utils/reactUtils/openModal/openModal';
import { CompareScanMetricsModal } from '../CompareScanMetricsModal/CompareScanMetricsModal';
import { InbodyIndicatorInfo } from '../../../../../interfaces/inbodyIndicator';
import { KeyBodyMetricsModal } from '../KeyBodyMetricsModal/KeyBodyMetricsModal';
import { SocialMediaScanMetricsModal } from '../SocialMediaScanMetricsModal/SocialMediaScanMetricsModal';
import { CustomerInfo } from '../NgzInbodyIndicatorsTab.settings';
import {
  CUSTOMER_TYPE,
  CustomerType,
} from '../../../../../../shared/constants/customer/customerType';

type InbodyIndicatorsOptionsProps = {
  customerInfo: CustomerInfo;
  customerInbodyTypeFlags: Flags<InbodyType>;
  availableScansMetadata: ScanMetadata[];
  customerInbodyIndicators: InbodyIndicatorInfo[];
  customerType: CustomerType;
  entityId?: number;
};

const InbodyIndicatorsOptions = ({
  customerInfo,
  customerInbodyTypeFlags,
  availableScansMetadata,
  customerInbodyIndicators,
  customerType,
  entityId,
}: InbodyIndicatorsOptionsProps) => {
  const hideScanSummaryButton = useMemo(() => {
    const isNotSupportedInbodyType = INBODY_TYPE_WITH_DISABLED_SCENARIO.some(
      (type) => type === customerInfo.inbodyType,
    );
    const isNotSupportedCustomerType = customerType === CUSTOMER_TYPE.SCAN_IQ;

    return isNotSupportedInbodyType || isNotSupportedCustomerType;
  }, [customerInfo.inbodyType, customerType]);

  const handleScanSummary = () => {
    openModal((closeModal) => (
      <ScanSummaryModal customerId={customerInfo.id} onClose={closeModal} />
    ));
  };

  const handleCompareScanMetrics = () => {
    openModal((closeModal) => (
      <CompareScanMetricsModal
        customerInfo={customerInfo}
        scansMetadata={availableScansMetadata}
        customerInbodyIndicators={customerInbodyIndicators}
        onClose={closeModal}
      />
    ));
  };

  const handleKeyBodyMetrics = () => {
    openModal((closeModal) => (
      <KeyBodyMetricsModal
        customerId={customerInfo.id}
        customerType={customerType}
        entityId={entityId}
        onClose={closeModal}
      />
    ));
  };

  const handleRequestSocialTemplate = () => {
    openModal((closeModal) => (
      <SocialMediaScanMetricsModal
        customerId={customerInfo.id}
        entityId={entityId}
        customerType={customerType}
        scansMetadata={availableScansMetadata}
        onClose={closeModal}
      />
    ));
  };

  const handlePrintReport = () => {
    window.document.body.setAttribute('style', 'width:285mm');
    setTimeout(() => {
      window.print();
      window.document.body.setAttribute('style', 'width:inherit');
    }, 200);
  };

  const menuOptions: MenuOptions[] = [
    {
      id: 'scan-summary',
      label: 'Scan Summary',
      onClick: handleScanSummary,
      isHide: hideScanSummaryButton,
    },
    {
      id: 'compare-scan-metrics',
      label: 'Compare Scan Metrics',
      onClick: handleCompareScanMetrics,
    },
    {
      id: 'key-body-metrics',
      label: 'Check Key Body Metrics',
      onClick: handleKeyBodyMetrics,
    },
    {
      id: 'create-social-post',
      label: 'Create Social Post',
      onClick: handleRequestSocialTemplate,
      isHide: customerInbodyTypeFlags.NON_INBODY,
    },
    { id: 'print-reports', label: 'Print Reports', onClick: handlePrintReport },
  ];

  return (
    <LbMenu
      options={menuOptions}
      buttonProps={{
        variant: 'contained',
        size: 'small',
        endIcon: <ArrowDropDownRoundedIcon />,
      }}
    >
      More actions
    </LbMenu>
  );
};

export { InbodyIndicatorsOptions };
